import {
  Badge,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { RiLockUnlockLine } from 'react-icons/ri';

import { DatePicker } from '../../../../../../../../../components/Form/DatePicker';
import {
  ActivityScheduleSessionBlockReason,
  IActivityScheduleSessionBlockBase,
  IBlockableActivitySchedule,
  WaveSideEnum,
} from '../../../../../../../../../models/activities';
import { createActivityScheduleSessionBlocksService } from '../../../../../../../../../services/Activities/CreateActivityScheduleSessionBlocksService';
import { listActivityScheduleSessionBlocksService } from '../../../../../../../../../services/Activities/ListActivityScheduleSessionBlocksService';
import { listBlockableActivitySchedulesService } from '../../../../../../../../../services/Activities/ListBlockableActivitySchedulesService';
import { unblockActivityScheduleSessionBlocksService } from '../../../../../../../../../services/Activities/UnblockActivityScheduleSessionBlocksService';
import { translateError } from '../../../../../../../../../utils/errors';

interface ISessionBlocksModalProps {
  activityId: string;
  isOpen: boolean;
  onClose(): void;
}

const CUSTOM_REASON_MAX_LENGTH = 25;

interface ISessionBlockFormData {
  blockedDate: Date;
  customReason?: string;
  reason: ActivityScheduleSessionBlockReason;
}

const getDefaultFormValues = (): ISessionBlockFormData => ({
  blockedDate: new Date(),
  customReason: '',
  reason: 'WEATHER_CONDITIONS',
});

const reasonOptions: Array<{
  label: string;
  value: ActivityScheduleSessionBlockReason;
}> = [
  { label: 'Condições climáticas', value: 'WEATHER_CONDITIONS' },
  { label: 'Falta de energia', value: 'POWER_OUTAGE' },
  { label: 'Problemas técnicos', value: 'TECHNICAL_ISSUES' },
  { label: 'Outros', value: 'OTHER' },
];

const reasonLabel = reasonOptions.reduce<
  Record<ActivityScheduleSessionBlockReason, string>
>((acc, option) => ({ ...acc, [option.value]: option.label }), {
  WEATHER_CONDITIONS: '',
  POWER_OUTAGE: '',
  TECHNICAL_ISSUES: '',
  OTHER: '',
});

const toApiDate = (date: Date): string => date.toLocaleDateString('fr-CA');

export const SessionBlocksModal = ({
  activityId,
  isOpen,
  onClose,
}: ISessionBlocksModalProps): JSX.Element => {
  const toast = useToast();
  const [activeBlocks, setActiveBlocks] = useState<
    IActivityScheduleSessionBlockBase[]
  >([]);
  const [schedules, setSchedules] = useState<IBlockableActivitySchedule[]>([]);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<string[]>([]);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors },
  } = useForm<ISessionBlockFormData>({
    defaultValues: getDefaultFormValues(),
  });

  const selectedDate = watch('blockedDate');
  const selectedReason = watch('reason');
  const customReasonValue = watch('customReason') ?? '';

  const loadBlocks = useCallback(async () => {
    try {
      setIsLoadingBlocks(true);
      const today = toApiDate(new Date());
      const data = await listActivityScheduleSessionBlocksService({
        activityId,
        fromDate: today,
      });

      setActiveBlocks(data);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao carregar paralisações',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao carregar as paralisações.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    } finally {
      setIsLoadingBlocks(false);
    }
  }, [activityId, toast]);

  const loadSchedules = useCallback(
    async (date: Date) => {
      try {
        setIsLoadingSchedules(true);
        setSelectedScheduleIds([]);

        const data = await listBlockableActivitySchedulesService({
          activityId,
          blockedDate: toApiDate(date),
        });

        setSchedules(data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar sessões',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar as sessões da data selecionada.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } finally {
        setIsLoadingSchedules(false);
      }
    },
    [activityId, toast],
  );

  useEffect(() => {
    if (isOpen) {
      loadBlocks();
    }
  }, [isOpen, loadBlocks]);

  useEffect(() => {
    if (isOpen && selectedDate) {
      loadSchedules(selectedDate);
    }
  }, [isOpen, loadSchedules, selectedDate]);

  const availableSchedules = useMemo(
    () => schedules.filter((schedule) => !schedule.isBlocked),
    [schedules],
  );
  const availableScheduleIds = useMemo(
    () => availableSchedules.map((schedule) => schedule.activityScheduleId),
    [availableSchedules],
  );
  const selectedAvailableScheduleIds = useMemo(
    () =>
      selectedScheduleIds.filter((scheduleId) =>
        availableScheduleIds.includes(scheduleId),
      ),
    [availableScheduleIds, selectedScheduleIds],
  );
  const isAllAvailableSchedulesSelected =
    availableScheduleIds.length > 0 &&
    selectedAvailableScheduleIds.length === availableScheduleIds.length;
  const isSomeAvailableSchedulesSelected =
    selectedAvailableScheduleIds.length > 0 && !isAllAvailableSchedulesSelected;

  const handleToggleSchedule = useCallback((scheduleId: string) => {
    setSelectedScheduleIds((prevState) =>
      prevState.includes(scheduleId)
        ? prevState.filter((id) => id !== scheduleId)
        : [...prevState, scheduleId],
    );
  }, []);

  const handleToggleAllAvailableSchedules = useCallback(() => {
    setSelectedScheduleIds(
      selectedAvailableScheduleIds.length === availableScheduleIds.length
        ? []
        : availableScheduleIds,
    );
  }, [availableScheduleIds, selectedAvailableScheduleIds.length]);

  const handleClose = useCallback(() => {
    reset(getDefaultFormValues());
    setSchedules([]);
    setSelectedScheduleIds([]);
    onClose();
  }, [onClose, reset]);

  const handleCreateBlock = useCallback(
    async ({ blockedDate, customReason, reason }: ISessionBlockFormData) => {
      if (!selectedScheduleIds.length) {
        toast({
          title: 'Selecione ao menos uma sessão',
          status: 'warning',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
        return;
      }

      if (reason === 'OTHER' && !customReason?.trim()) {
        toast({
          title: 'Informe o motivo',
          description: 'O motivo customizado é obrigatório para Outro.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
        return;
      }

      if (
        reason === 'OTHER' &&
        (customReason?.trim().length ?? 0) > CUSTOM_REASON_MAX_LENGTH
      ) {
        toast({
          title: 'Motivo muito longo',
          description: `O motivo customizado deve ter no máximo ${CUSTOM_REASON_MAX_LENGTH} caracteres.`,
          status: 'warning',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
        return;
      }

      try {
        setIsSubmitting(true);

        await createActivityScheduleSessionBlocksService({
          activityId,
          activityScheduleIds: selectedScheduleIds,
          blockedDate: toApiDate(blockedDate),
          customReason,
          reason,
        });

        toast({
          title: 'Paralisação criada',
          description: 'As sessões selecionadas foram paralisadas.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        reset({
          blockedDate,
          customReason: '',
          reason: 'WEATHER_CONDITIONS',
        });
        setSelectedScheduleIds([]);
        await Promise.all([loadBlocks(), loadSchedules(blockedDate)]);

        window.dispatchEvent(
          new CustomEvent('activity-session-blocks:changed', {
            detail: { activityId },
          }),
        );
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao criar paralisação',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao criar a paralisação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [activityId, loadBlocks, loadSchedules, reset, selectedScheduleIds, toast],
  );

  const handleUnblock = useCallback(
    async (blockId: string) => {
      try {
        setIsSubmitting(true);
        await unblockActivityScheduleSessionBlocksService({
          activityId,
          blockId,
        });

        toast({
          title: 'Paralisação revertida',
          description: 'Os usuários com reservas futuras serão notificados.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        await loadBlocks();
        if (selectedDate) await loadSchedules(selectedDate);

        window.dispatchEvent(
          new CustomEvent('activity-session-blocks:changed', {
            detail: { activityId },
          }),
        );
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao reverter paralisação',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao reverter a paralisação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [activityId, loadBlocks, loadSchedules, selectedDate, toast],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Gerenciar paralisações</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Stack spacing="8">
            <Flex direction="column">
              <Text fontWeight="semibold" mb="4">
                Paralisações ativas
              </Text>

              {isLoadingBlocks ? (
                <Flex justify="center" py="8">
                  <Spinner />
                </Flex>
              ) : (
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Data</Th>
                      <Th>Motivo</Th>
                      <Th>Sessões</Th>
                      <Th>Reservas</Th>
                      <Th>Fila</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {activeBlocks.map((block) => (
                      <Tr key={block.id}>
                        <Td>{block.blockedDate}</Td>
                        <Td>
                          {block.reason === 'OTHER'
                            ? block.customReason
                            : reasonLabel[block.reason]}
                        </Td>
                        <Td>
                          {block.items
                            .map(
                              (item) =>
                                `${item.startTimeSnapshot.slice(
                                  0,
                                  5,
                                )} - ${item.endTimeSnapshot.slice(0, 5)}`,
                            )
                            .join(', ')}
                        </Td>
                        <Td>{block.bookings.length}</Td>
                        <Td>{block.queues.length}</Td>
                        <Td textAlign="right">
                          <Button
                            colorScheme="green"
                            isDisabled={isSubmitting}
                            leftIcon={<Icon as={RiLockUnlockLine} />}
                            onClick={() => handleUnblock(block.id)}
                            size="xs"
                          >
                            Reverter
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Flex>

            <Divider />

            <Flex
              as="form"
              direction="column"
              onSubmit={handleSubmit(handleCreateBlock)}
            >
              <Text fontWeight="semibold" mb="4">
                Nova paralisação
              </Text>

              <Stack spacing="6">
                <DatePicker
                  control={control}
                  error={errors.blockedDate}
                  isClearable={false}
                  label="Data"
                  minDate={new Date()}
                  name="blockedDate"
                />

                <FormControl>
                  <FormLabel>Motivo</FormLabel>
                  <Select {...register('reason')}>
                    {reasonOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {selectedReason === 'OTHER' && (
                  <FormControl>
                    <FormLabel>Motivo customizado</FormLabel>
                    <Textarea
                      {...register('customReason')}
                      maxLength={CUSTOM_REASON_MAX_LENGTH}
                    />
                    <Text color="gray.500" fontSize="xs" mt="1">
                      {`${customReasonValue.length}/${CUSTOM_REASON_MAX_LENGTH}`}
                    </Text>
                  </FormControl>
                )}

                <Flex direction="column">
                  <HStack justifyContent="space-between" mb="3">
                    <Text fontWeight="medium">Sessões afetadas</Text>
                  </HStack>

                  {isLoadingSchedules ? (
                    <Flex justify="center" py="8">
                      <Spinner />
                    </Flex>
                  ) : (
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>
                            {!!availableSchedules.length && (
                              <Checkbox
                                isChecked={isAllAvailableSchedulesSelected}
                                isDisabled={isLoadingSchedules || isSubmitting}
                                isIndeterminate={
                                  isSomeAvailableSchedulesSelected
                                }
                                onChange={handleToggleAllAvailableSchedules}
                              />
                            )}
                          </Th>
                          <Th>Horário</Th>
                          <Th>Modalidade</Th>
                          <Th>Reservas</Th>
                          <Th>Fila</Th>
                          <Th>Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {schedules.map((schedule) => (
                          <Tr key={schedule.activityScheduleId}>
                            <Td>
                              <Checkbox
                                isChecked={selectedScheduleIds.includes(
                                  schedule.activityScheduleId,
                                )}
                                isDisabled={schedule.isBlocked}
                                onChange={() =>
                                  handleToggleSchedule(
                                    schedule.activityScheduleId,
                                  )
                                }
                              />
                            </Td>
                            <Td>
                              {schedule.startTime.slice(0, 5)}
                              {' - '}
                              {schedule.endTime.slice(0, 5)}
                            </Td>
                            <Td>
                              <HStack spacing="2">
                                <Text>
                                  {schedule.modalityTitle || 'Sem modalidade'}
                                </Text>
                                {schedule.waveSide && (
                                  <Badge>
                                    {WaveSideEnum[schedule.waveSide]}
                                  </Badge>
                                )}
                              </HStack>
                            </Td>
                            <Td>{schedule.booked}</Td>
                            <Td>{schedule.queueBooked}</Td>
                            <Td>
                              <Badge
                                colorScheme={
                                  schedule.isBlocked ? 'red' : 'green'
                                }
                              >
                                {schedule.isBlocked ? 'Paralisada' : 'Livre'}
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}

                  {!isLoadingSchedules && !availableSchedules.length && (
                    <Text color="gray.500" fontSize="sm" mt="4">
                      Nenhuma sessão disponível para a data selecionada.
                    </Text>
                  )}
                </Flex>
              </Stack>

              <ModalFooter px="0" pb="0">
                <Button mr="3" onClick={handleClose} variant="ghost">
                  Fechar
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  type="submit"
                >
                  Paralisar sessões
                </Button>
              </ModalFooter>
            </Flex>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
